
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Campaign } from "@/types/campaign";
import { CampaignsTable } from "@/components/campaigns/CampaignsTable";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { CampaignFormDialog } from "@/components/campaigns/CampaignFormDialog";
import { AssignNumbersDialog } from "@/components/campaigns/AssignNumbersDialog";
import { useNavigate } from "react-router-dom";

export default function CampaignsPage() {
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_campaigns')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Campaign[];
    }
  });

  const { data: links } = useQuery({
    queryKey: ['campaign-number-links'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_campaign_numbers')
        .select('campaign_id');
      if (error) throw error;
      return (data || []) as { campaign_id: string }[];
    }
  });

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    (links || []).forEach(l => { map[l.campaign_id] = (map[l.campaign_id] || 0) + 1; });
    return map;
  }, [links]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const tenantId = (user as any)?.app_metadata?.tenant_id as string | undefined;

  useEffect(() => {
    const title = 'Campaigns | Calie.ai';
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Manage Calie.ai campaigns: create, edit, assign numbers, and control status.');
    else {
      const m = document.createElement('meta');
      m.name = 'description';
      m.content = 'Manage Calie.ai campaigns: create, edit, assign numbers, and control status.';
      document.head.appendChild(m);
    }
    const linkRel = 'canonical';
    let canonical = document.querySelector(`link[rel="${linkRel}"]`) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = linkRel;
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + '/campaigns';
  }, []);

  const [formOpen, setFormOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);

  const navigate = useNavigate();
  const onEdit = (c: Campaign) => { setSelectedCampaign(c); setFormOpen(true); };
  const onAssign = (c: Campaign) => { navigate(`/campaigns/${c.campaign_id}`); };
  const onCreate = () => { setSelectedCampaign(null); setFormOpen(true); };
  const { mutate: mutateStatus } = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: Campaign['status'] }) => {
      const { error } = await supabase
        .from('calie_campaigns')
        .update({ status })
        .eq('campaign_id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast({ title: 'Status updated' });
    },
    onError: (e: any) => toast({ title: 'Failed to update status', description: e.message, variant: 'destructive' })
  });

  return (
    <MainLayout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Create and manage your tracking campaigns.</p>
          <div className="flex items-center gap-2">
            <Button onClick={onCreate} disabled={loading || !tenantId}>New Campaign</Button>
            {!loading && !tenantId && <span className="text-xs text-muted-foreground">Sign in to create campaigns</span>}
          </div>
        </div>
        {isLoading ? (
          <div>Loading campaigns...</div>
        ) : campaigns?.length ? (
          <CampaignsTable 
            campaigns={campaigns}
            counts={counts}
            onEdit={onEdit}
            onAssign={onAssign}
            onChangeStatus={(id, status) => mutateStatus({ id, status })}
          />
        ) : (
          <div className="border rounded-md p-6 text-center">
            <div className="text-lg font-medium mb-2">No campaigns yet</div>
            <p className="text-muted-foreground mb-4">Create your first campaign to get started.</p>
            
          </div>
        )}
      </div>

      <CampaignFormDialog 
        open={formOpen}
        onOpenChange={setFormOpen}
        tenantId={tenantId || null}
        initial={selectedCampaign}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        }}
      />

      <AssignNumbersDialog 
        open={assignOpen}
        onOpenChange={setAssignOpen}
        tenantId={tenantId || null}
        campaign={selectedCampaign}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['campaign-number-links'] });
        }}
      />
    </MainLayout>
  );
}
